<template>
  <Toast
    class="communication"
    hide-close-btn
    :variant="color.value"
    :show-icon="false"
    :class="{ collapsed: toastCollapsed, }"
  >
    <template #title>
      <div class="info-header">
        <InputClassic
          v-if="editing"
          v-model="title"
          :label="$t('communications.titre')"
          status="required"
          :max-length="255"
        />
        <strong
          v-else
          class="text-regular bold title"
        >
          {{ title }}
        </strong>
        <div class="buttons">
          <ButtonClassic
            v-if="!editing && false"
            variant="special"
            color="primary"
            icon="right"
            size="medium"
            :label="toastCollapsed ? $t('communications.developper') : $t('communications.reduire')"
            :aria-expanded="!toastCollapsed"
            @click="toggleToast"
          >
            <template #right-icon>
              <component :is="toastCollapsed ? UilAngleDown : UilAngleUp" />
            </template>
          </ButtonClassic>
          <ButtonClassic
            v-if="!editing && $store.getters.isMaitreComptaRegion"
            variant="special"
            color="primary"
            icon="left"
            size="medium"
            :aria-label="$t('action.modifier')"
            @click="startEditing"
          >
            <template #left-icon>
              <UilPen />
            </template>
          </ButtonClassic>
        </div>
      </div>
    </template>
    <template
      v-if="!toastCollapsed"
      #content
    >
      <div
        v-if="!editing"
        class="ql-editor readonly text-medium"
        v-html="content"
      />
      <QuillEditor
        v-else
        ref="editor"
        v-model:content="content"
        theme="snow"
        content-type="html"
        placeholder="Saisissez le texte ici"
        :toolbar="toolbar"
        :modules="modules"
      />

      <template v-if="editing">
        <div class="colors">
          <SelectClassic
            v-model="color"
            :label="$t('communications.couleur-label')"
            :options="localizedColors"
          />
        </div>
        <div class="text-regular visibility">
          {{ $t('communications.affichage-pages') }}
          <ul
            v-for="pageList in pages"
            :key="pageList[0].key"
          >
            <li
              v-for="page in pageList"
              :key="page.key"
            >
              <ButtonCheckbox
                v-model="page.checked"
                :label="$t(page.labelKey)"
                @update:modelValue="gererLesPages(page)"
              />
              <ul v-if="page.subPages">
                <li
                  v-for="subPage in page.subPages"
                  :key="subPage.key"
                >
                  <ButtonCheckbox
                    v-model="subPage.checked"
                    :label="$t(subPage.labelKey)"
                    @update:modelValue="gererLesPages(subPage)"
                  />
                </li>
              </ul>
            </li>
          </ul>
        </div>
        <div class="footer-buttons">
          <ButtonClassic
            variant="solid"
            color="error"
            icon="left"
            size="medium"
            :label="$t('action.supprimer')"
            @click="deleteMessage"
          >
            <template #left-icon>
              <UilTrashAlt />
            </template>
          </ButtonClassic>
          <ButtonClassic
            v-if="message.id"
            variant="solid"
            :label="$t('action.annuler')"
            color="secondary"
            icon="right"
            @click="cancelEditing"
          >
            <template #right-icon>
              <UilTimes />
            </template>
          </ButtonClassic>
          <ButtonClassic
            variant="solid"
            :label="$t('action.enregistrer')"
            color="primary"
            icon="right"
            @click="save"
          >
            <template #right-icon>
              <UilCheckCircle />
            </template>
          </ButtonClassic>
        </div>
      </template>
    </template>
  </Toast>
</template>

<script lang="ts" setup>
// #region imports =======================
import {
  ref, computed, watch, onMounted,
} from "vue";
import { useI18n } from "vue-i18n";
import { QuillEditor } from "@vueup/vue-quill";
import BlotFormatter from "quill-blot-formatter";

import "@vueup/vue-quill/dist/vue-quill.snow.css";
import {
  Toast, ButtonClassic, ButtonCheckbox, SelectClassic, InputClassic,
} from "@lde/core_lde_vue";
import {
  UilPen, UilCheckCircle, UilTimes, UilAngleUp, UilAngleDown, UilTrashAlt,
} from "@lde/vue-unicons";

import CommunicationModel from "@/domains/communications/communication";
import { useToast } from "@/utils/toast";

import { buildPages, Page, SubPage } from "./pages";
// #endregion

defineOptions({ name: "Communication" });

// #region Général =======================

const emit = defineEmits<{
  delete: [message: CommunicationModel],
  save: [message: CommunicationModel],
  editing: [value: boolean],
}>();
const { t } = useI18n();
const editing = ref(false);
const message = defineModel<CommunicationModel>({ required: true });
const title = ref(message.value.titre || t("communications.titre"));
watch(message, () => {
  if (message.value.texte === "") {
    editing.value = true;
  }
}, { immediate: true, deep: true });

watch(editing, () => {
  if (editing.value) {
    // En mode édition, on force l'affichage complet du message pour faciliter la modification
    toastCollapsed.value = false;
  }
});
const toast = useToast();

// #endregion
// #region Éditeur de texte pour le message =======================
const editor = ref<InstanceType<typeof QuillEditor> | null>(null);
const content = ref(message.value.texte || "");
const toolbar = [
  ["bold", "italic", "underline", "strike"],
  ["link", "image", "video"],
  [{ list: "ordered" }, { list: "bullet" }, { list: "check" }],
  [{ indent: "-1" }, { indent: "+1" }],
  [{ header: [1, 2, 3, 4, 5, 6, false] }],
  [{ color: [] }, { background: [] }],
  [{ align: [] }],
  ["clean"],
] as const;
const modules = [{
  name: "blotFormatter",
  module: BlotFormatter,
}];

// #endregion
// #region Choix des pages où afficher le message =======================

const pages = ref<Page[][]>(buildPages(message.value.pages || []));

/**
 * Au render, propage uniquement l'état de "Toutes les pages" si cette case est cochée.
 * Cela évite d'écraser la sélection réelle des pages d'un message existant.
 */
onMounted(() => {
  const allPage = pages.value.flat().find((p) => p.key === "all");
  if (allPage?.checked) {
    pages.value.forEach((pageList) => {
      pageList.forEach((p) => {
        p.checked = allPage.checked;
        p.subPages?.forEach((sp) => { sp.checked = allPage.checked; });
      });
    });
  }
});

/**
 * Gère la logique de cochage/décochage des pages et sous-pages.
 * - Si "Toutes les pages" est coché/décoché, toutes les pages suivent.
 * - Si une page parente est cochée, ses sous-pages suivent.
 * - Si toutes les sous-pages sont cochées, la page parente est cochée.
 * - Si toutes les pages individuelles sont cochées, "Toutes les pages" est coché.
 */
const gererLesPages = (page: Page | SubPage) => {
  if (page.key === "all") {
    pages.value.forEach((pageList) => {
      pageList.forEach((p) => {
        p.checked = page.checked;
        p.subPages?.forEach((sp) => { sp.checked = page.checked; });
      });
    });
    return;
  }

  if ("subPages" in page && page.subPages) {
    page.subPages.forEach((sp) => { sp.checked = page.checked; });
  } else {
    const parentPage = pages.value
      .flat()
      .find((p) => p.subPages?.some((sp) => sp.key === page.key));
    if (parentPage) {
      parentPage.checked = parentPage.subPages?.every((sp) => sp.checked) ?? false;
    }
  }

  // Mettre à jour "Toutes les pages" en fonction de l'état global
  const allPage = pages.value.flat().find((p) => p.key === "all");
  if (allPage) {
    allPage.checked = pages.value
      .flat()
      .filter((p) => p.key !== "all")
      .every((p) => p.checked && (!p.subPages || p.subPages.every((sp) => sp.checked)));
  }
};

// #endregion
// #region Choix de la couleur du message =======================

interface ColorOption {
  labelKey: string;
  value: string;
}

const colors: readonly ColorOption[] = [
  { labelKey: "general.bleu", value: "info" },
  { labelKey: "general.jaune", value: "warning" },
  { labelKey: "general.rouge", value: "error" },
  { labelKey: "general.vert", value: "success" },
] as const;

/** Options de couleurs traduites pour le SelectClassic */
const localizedColors = computed(() => colors.map((c) => ({ label: t(c.labelKey), value: c.value })));

const initialColor = colors.find((c) => c.value === message.value.couleur) || colors[0];
const color = ref<{ label: string; value: string }>({
  label: t(initialColor.labelKey),
  value: initialColor.value,
});

// #endregion
// #region Gestion du collapse du Toast =======================

const COLLAPSED_STORAGE_KEY = "communications_collapsed";

/**
 * Récupère l'état de collapse depuis le localStorage.
 * Le format stocké est un objet { [id]: dateModificationAuMomentDuCollapse }.
 * Si le message a été modifié après le collapse, on le réouvre automatiquement.
 */
const getInitialCollapsed = (): boolean => {
  if (!message.value?.id) return false;
  try {
    const stored = JSON.parse(localStorage.getItem(COLLAPSED_STORAGE_KEY) || "{}");
    const collapsedAt = stored[message.value.id];
    if (!collapsedAt) return false;
    // Si le message a été modifié après le dernier collapse, on le réouvre
    if (message.value.date_modification && new Date(message.value.date_modification) > new Date(collapsedAt)) {
      delete stored[message.value.id];
      localStorage.setItem(COLLAPSED_STORAGE_KEY, JSON.stringify(stored));
      return false;
    }
    return true;
  } catch {
    return false;
  }
};

const toastCollapsed = ref(getInitialCollapsed());

const toggleToast = () => {
  toastCollapsed.value = !toastCollapsed.value;
  if (!message.value?.id) return;
  try {
    const stored = JSON.parse(localStorage.getItem(COLLAPSED_STORAGE_KEY) || "{}");
    if (toastCollapsed.value) {
      stored[message.value.id] = new Date().toISOString();
    } else {
      delete stored[message.value.id];
    }
    localStorage.setItem(COLLAPSED_STORAGE_KEY, JSON.stringify(stored));
  } catch { /* silently ignore localStorage errors */ }
};

// #endregion
// #region Mode édition : entrer, annuler, sauvegarder =======================
let backupTitle = "";
let backupContent = "";
let backupColor = { label: "", value: "" };
let backupPages = "";

/** Entre en mode édition et sauvegarde l'état actuel pour pouvoir annuler */
const startEditing = () => {
  backupTitle = title.value;
  backupContent = content.value;
  backupColor = { ...color.value };
  backupPages = JSON.stringify(pages.value);
  editing.value = true;
  emit("editing", true);
};

/** Annule les modifications et restaure l'état précédent */
const cancelEditing = () => {
  title.value = backupTitle;
  content.value = backupContent;
  color.value = { ...backupColor };
  pages.value = backupPages ? JSON.parse(backupPages) : [];
  editing.value = false;
  emit("editing", false);
};

const _getSelectedPages = (): string[] => {
  const selectedPagesFlat = Array.from(
    pages.value.flat().reduce((acc: Set<string>, page) => {
      if (page.checked) {
        acc.add(page.key);
      }
      if (page.subPages) {
        page.subPages.forEach((sp) => {
          if (sp.checked) {
            acc.add(sp.key);
          }
        });
      }
      return acc;
    }, new Set<string>()),
  );

  if (selectedPagesFlat.length === 0) {
    // Si aucune page n'est sélectionnée, on considère que le message doit s'afficher sur toutes les pages
    selectedPagesFlat.push("all");
  }
  return selectedPagesFlat;
};

/** Sauvegarde les communications */
const save = async () => {
  // Workaround : l'alignement des images ne met pas à jour le content via v-model
  // si c'est la dernière action effectuée. On force la mise à jour.
  if (!editor.value) return;
  content.value = editor.value.getHTML();

  const res = await CommunicationModel.updateOrCreate({
    id: message.value?.id,
    titre: title.value,
    texte: content.value,
    couleur: color.value.value,
    pages: _getSelectedPages(),
  });
  if (!res) return;

  message.value = res;
  editing.value = false;
  emit("save", message.value);
  toast.success({ title: t("communications.message-sauvegarde") });
};

/** Supprime la communication */
const deleteMessage = async () => {
  if (message.value?.id) {
    await CommunicationModel.delete(message.value.id);
  }
  toast.success({ title: t("communications.message-supprime") });
  emit("delete", message.value);
};
// #endregion
</script>

<style lang="scss">
@use "~@lde/core_lde_vue/dist/assets/styles/_variables.scss" as var;
@use "~@lde/core_lde_vue/dist/assets/styles/_media_mixins.scss" as media;

.toast.communication {
  padding: var.$space-small;
  align-items: flex-start;

  .status-bar {
    display: none;
  }
  .ql-toolbar {
    margin-top: var.$space-x-tiny;
    border-radius: var.$border-radius-base var.$border-radius-base 0 0;
  }
  .text-container {
    margin-right: 0;
  }
  .ql-container {
    border-radius: 0 0 var.$border-radius-base var.$border-radius-base;
  }
  .ql-editor {
    margin-right: var.$space-small;
    margin-bottom: var.$space-x-tiny;
    &.readonly {
      padding: 0;
    }
  }
  .info-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    .input-classic {
      width: 75%;
    }
  }
  .buttons, .footer-buttons {
    display: flex;
    gap: var.$space-xx-tiny;
    justify-content: flex-end;
  }
  .footer-buttons {
    margin-top: var.$space-x-tiny;
  }

  img {
    max-width: 100%;
    height: auto;
  }

  .colors {
    margin-top: var.$space-tiny;
  }
  .visibility {
    margin-top: var.$space-tiny;

    ul {
      list-style: none;
    }
    > ul {
      padding: 0;
      display: flex;
      flex-wrap: wrap;
      > li {
        width: 270px;
        @include media.small-screen { width: 100%; }

        > ul {
          position: relative;
          padding-left: var.$space-small;
          &::before {
            position: absolute;
            top: 0;
            left: 11px;
            content: "";
            height: 90%;
            border: 1px solid var.$gray-light;
          }
        }
      }
    }
  }
}
</style>
